<template>
  <!--
    The contributor drawer. One column, section-labelled, in the same idiom as the Foodleap
    contributor cards that open it — see FoodleapSimplifiedActorDetail's contacts section.

    Accents run through the `primary` token rather than a literal green: this panel is opened from
    settings, communities, comments, announcements and conversations in every ecosystem, so a
    hardcoded palette would repaint all of them in one ecosystem's colours. The activity dot is the
    exception — it signals status, not brand.
  -->
  <div class="user-information h-full overflow-y-auto bg-white">
    <div class="p-6" v-if="isUserDataLoading">
      <loading/>
    </div>

    <!--
      An empty response, which `UserController::show` returns as a bare `[]` for a user it will not
      describe: no role in this ecosystem, or an actor-role user in a private one (see
      User::hasRole called with no arguments). Rendering the body against that produced a nameless
      profile with "—" for every fact, which reads as broken data rather than as no access.
    -->
    <div class="p-6 sm:p-8" v-else-if="!user || !user.id">
      <div class="tw-flex justify-end">
        <button
          class="tw-flex items-center justify-center w-8 h-8 rounded-full border-0 bg-transparent
                 text-slate-700 cursor-pointer transition-colors hover:bg-slate-100"
          type="button"
          :aria-label="$t('user_panel_close')"
          @click="hideUserInformationPanel"
        >
          <icon name="x" size="18"/>
        </button>
      </div>
      <p class="m-0 mt-4 text-sm text-slate-500">{{ $t('user_panel_unavailable') }}</p>
    </div>

    <div class="p-6 sm:p-8" v-else>
      <div class="tw-flex justify-end">
        <button
          class="tw-flex items-center justify-center w-8 h-8 rounded-full border-0 bg-transparent
                 text-slate-700 cursor-pointer transition-colors hover:bg-slate-100
                 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          type="button"
          :aria-label="$t('user_panel_close')"
          @click="hideUserInformationPanel"
        >
          <icon name="x" size="18"/>
        </button>
      </div>

      <!-- ── Identity ── -->
      <div class="tw-flex gap-5 items-center mt-1">
        <!--
          Written out rather than using <avatar>: that component sizes `.avatar__img` from a global
          22px variable, so passing it a 96px width grew the circle and left the photo a thumbnail
          inside it. A background-image fills the circle at any size, and it is the same treatment
          the contributor tiles that open this drawer already use.
        -->
        <span
          class="shrink-0 tw-flex items-center justify-center w-28 h-28 rounded-full
                 bg-primary-extra-lightest bg-cover bg-center text-3xl font-semibold text-slate-700"
          :style="user.photo_url ? { backgroundImage: `url(${user.photo_url})` } : null"
          :title="user.name"
          aria-hidden="true"
        >{{ user.photo_url ? '' : userInitials }}</span>
        <div class="min-w-0">
          <h3 class="m-0 text-2xl font-bold leading-tight tracking-tight text-slate-800 break-words">
            {{ user.name }}
          </h3>
          <p class="mt-1 mb-0 text-sm text-slate-500" v-if="roleLine">{{ roleLine }}</p>
          <!-- The organisation this person represents: their first claimed actor, which is what the
               claimed-actors list below spells out in full. -->
          <p class="mt-2 mb-0 text-sm font-medium text-primary" v-if="primaryOrganisation">
            {{ primaryOrganisation.name }}
          </p>
        </div>
      </div>

      <!-- ── Actions ── -->
      <div class="tw-flex gap-3 mt-7" v-if="canStartConversation || socialLinks.length">
        <button
          class="flex-1 rounded-md border-0 bg-slate-800 px-4 py-3 text-sm font-medium text-white
                 font-[inherit] cursor-pointer transition-opacity hover:opacity-90
                 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
                 disabled:opacity-60 disabled:cursor-default"
          type="button"
          v-if="canStartConversation"
          :disabled="isStartingConversation"
          @click="startConversation"
        >
          {{ $t('conversation_start') }}
        </button>
        <a
          class="tw-flex items-center justify-center w-12 rounded-md border border-slate-200
                 text-slate-700 no-underline transition-colors hover:border-primary hover:text-primary"
          v-for="link of socialLinks"
          :key="link.icon"
          target="_blank"
          rel="noopener"
          :href="link.url"
          :title="link.label"
          :aria-label="link.label"
          @click="trackSocialLink(link)"
        >
          <icon :name="link.icon" size="18"/>
        </a>
      </div>

      <div class="my-8 border-t border-slate-200"></div>

      <!-- ── About ── -->
      <template v-if="getUserBioI18n">
        <span :class="labelClass">{{ $t('actor_detail_about') }}</span>
        <p
          class="mt-3 mb-0 text-sm leading-6 text-slate-600 whitespace-pre-line"
          :class="{ 'line-clamp-4': !showFullBio }"
        >{{ getUserBioI18n }}</p>
        <button
          class="mt-3 border-0 bg-transparent p-0 font-[inherit] text-sm underline underline-offset-4
                 text-slate-700 cursor-pointer hover:text-primary"
          type="button"
          v-if="bioIsTruncatable"
          @click="toggleBio"
        >{{ showFullBio ? $t('user_panel_show_less') : $t('user_panel_view_full_bio') + ' ↗' }}
        </button>
      </template>
      <p class="m-0 text-sm text-slate-500" v-else>{{ $t('user_panel_profile_incomplete') }}</p>

      <!-- ── Expertise ── -->
      <div class="mt-9" v-if="expertiseTags.length">
        <span :class="labelClass">{{ $t('user_panel_expertise') }}</span>
        <div class="tw-flex flex-wrap gap-2 mt-3">
          <span
            class="rounded-md bg-primary-extra-lightest px-3 py-2 text-xs text-slate-700"
            v-for="tag of expertiseTags"
            :key="tag"
          >{{ tag }}</span>
        </div>
      </div>

      <!-- ── Motivation ── -->
      <div class="mt-10" v-if="motivationItems.length">
        <span :class="labelClass">{{ $t('user_panel_on_platform_for', { ecosystem: ecosystemName }) }}</span>
        <div class="tw-flex flex-col gap-5 mt-5">
          <div
            class="tw-flex gap-4 items-center text-sm text-slate-700"
            v-for="item of motivationItems"
            :key="item"
          >
            <icon class="shrink-0 text-primary" name="check" size="16"/>
            <span>{{ item }}</span>
          </div>
        </div>
      </div>

      <!--
        ── Contact details ──
        Department and function are not repeated here: they are the header's subtitle now, which is
        where the design puts the person's position.
      -->
      <div class="mt-10" v-if="hasContactDetails">
        <span :class="labelClass">{{ $t('user_panel_details') }}</span>
        <div class="grid grid-cols-2 gap-4 mt-4 text-sm">
          <!-- The phone number keeps its reveal control: it is the one field on this panel that is
               deliberately not readable at a glance. -->
          <div v-if="user.phone">
            <div class="text-slate-500">{{ $t('user_panel_phone_number') }}</div>
            <div class="tw-flex items-center gap-2 mt-1">
              <span
                class="text-slate-800 transition-all"
                :class="{
                  'blur-[5px] select-none': hidePhoneNumber,
                  'rounded bg-primary-extra-lightest px-1': highlightsPhoneNumber,
                }"
              >{{ user.phone }}</span>
              <button
                class="tw-flex items-center border-0 bg-transparent p-0 text-slate-500 cursor-pointer
                       hover:text-primary"
                type="button"
                v-if="!highlightsPhoneNumber"
                :aria-label="hidePhoneNumber
                  ? $t('user_panel_show_phone_number')
                  : $t('user_panel_hide_phone_number')"
                @click="toggleShow"
              >
                <icon :name="showEyeIcon" size="16"/>
              </button>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Weekly digest ── -->
      <div class="mt-10" v-if="isOwner || isMember">
        <span :class="labelClass">{{ $t('user_panel_weekly_digest') }}</span>
        <div class="tw-flex items-center justify-between gap-3 mt-3">
          <p class="m-0 text-sm text-slate-800">
            {{ user.weeklyDigest ? $t('user_panel_subscribed') : $t('user_panel_not_subscribed') }}
          </p>
          <DsButton
            v-if="user.weeklyDigest"
            :label="$t('user_panel_unsubscribe')"
            variant="secondary"
            size="small"
            @click="openUnsubscribeWeeklyDigestModal"
          />
        </div>
      </div>

      <!--
        ── Representing / activity ──
        The design's closing pair, though only the activity half is always there. Both are real:
        the "since" is the claim's own created_at, and the activity line is `last_activity` reduced
        to whether it falls in the current month — a coarser fact than the exact login timestamp,
        which stays in the owner-only block below.
      -->
      <div class="my-8 border-t border-slate-200"></div>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <!--
          Only for someone who actually represents an organisation. Without a claim this rendered
          "Representing an organisation since —", a sentence about nothing: the placeholder had
          been filled with its own fallback string. Ecosystem members hold no actor claims as a
          rule, so that was the common case rather than the edge, and the honest thing to show is
          the activity line alone.
        -->
        <div v-if="primaryOrganisation">
          <div class="text-slate-500">
            {{ $t('user_panel_representing_since', { organisation: primaryOrganisation.name }) }}
          </div>
          <div class="mt-1 text-slate-800">{{ representingSince || '—' }}</div>
        </div>
        <div :class="primaryOrganisation ? 'text-right' : ''">
          <div class="text-slate-500">{{ $t('user_panel_activity') }}</div>
          <div class="mt-1 text-slate-800">
            {{ activityLabel }}
            <!--
              The one accent that is deliberately not the `primary` token: this dot means "active",
              a status rather than a brand accent, and green reads that way in every ecosystem. On a
              brand-coloured dot the same shape would mean nothing consistent across tenants.
            -->
            <span
              class="inline-block ml-2 w-2 h-2 rounded-full bg-green-500 align-middle"
              v-if="isActiveThisMonth"
              aria-hidden="true"
            ></span>
          </div>
        </div>
      </div>

      <!--
        ── Membership ──
        Owners only, as before: role, tenure and last login are administrative facts about a member,
        not part of the outward-facing card.
      -->
      <template v-if="membershipRows.length">
        <div class="my-8 border-t border-slate-200"></div>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div v-for="row of membershipRows" :key="row.label">
            <div class="text-slate-500">{{ row.label }}</div>
            <div class="mt-1 text-slate-800">{{ row.value }}</div>
          </div>
        </div>
      </template>

      <!-- ── Claimed actors ── -->
      <template v-if="nonProductClaims.length">
        <div class="my-8 border-t border-slate-200"></div>
        <span :class="labelClass">{{ $t('profile_user_claimed_actors', { actors: actorsLabel }) }}</span>
        <div class="tw-flex flex-col gap-2 mt-4">
          <div
            class="tw-flex items-center gap-3 rounded-lg border border-slate-200 p-3"
            v-for="(claim, index) of slicedNonProductClaims"
            :key="'actor' + index"
          >
            <span
              class="shrink-0 tw-flex items-center justify-center w-9 h-9 rounded-md bg-slate-100
                     bg-contain bg-center bg-no-repeat text-xs font-semibold text-slate-500"
              :style="imageStyle(claim)"
              aria-hidden="true"
            >{{ companyLogoUrl(claim) ? '' : claimInitials(claim) }}</span>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium text-slate-800 truncate">{{ claim.name }}</div>
              <router-link
                class="text-xs text-primary no-underline hover:underline"
                :to="'/actors/' + claim.id"
                @click="trackClaimVisit('ClaimedActor', claim.name)"
              >{{ $t('profile_settings_actor_view') }}
              </router-link>
            </div>
          </div>
          <button
            class="self-start border-0 bg-transparent p-0 font-[inherit] text-xs text-primary
                   underline underline-offset-2 cursor-pointer"
            type="button"
            v-if="nonProductClaims.length > 5"
            @click="showMoreNonProductClaims = !showMoreNonProductClaims"
          >{{ showMoreNonProductClaims ? $t('actor_detail_show_less') : $t('actor_detail_show_all') }}
          </button>
        </div>
      </template>

      <!-- ── Claimed products ── -->
      <template v-if="productClaims.length">
        <div class="my-8 border-t border-slate-200"></div>
        <span :class="labelClass">{{ $t('profile_user_claimed_products', { products: productsLabel }) }}</span>
        <div class="tw-flex flex-col gap-2 mt-4">
          <div
            class="rounded-lg border border-slate-200 p-3"
            v-for="(claim, index) of slicedProductClaims"
            :key="'product' + index"
          >
            <div class="text-sm font-medium text-slate-800 truncate">{{ claim.name }}</div>
            <router-link
              class="text-xs text-primary no-underline hover:underline"
              :to="'/actors/' + claim.id"
              @click="trackClaimVisit('ClaimedProduct', claim.name)"
            >{{ $t('profile_settings_product_view') }}
            </router-link>
          </div>
          <button
            class="self-start border-0 bg-transparent p-0 font-[inherit] text-xs text-primary
                   underline underline-offset-2 cursor-pointer"
            type="button"
            v-if="productClaims.length > 5"
            @click="showMoreProductClaims = !showMoreProductClaims"
          >{{ showMoreProductClaims ? $t('actor_detail_show_less') : $t('actor_detail_show_all') }}
          </button>
        </div>
      </template>

      <!-- ── Communities ── -->
      <template v-if="user.communities && user.communities.length">
        <div class="my-8 border-t border-slate-200"></div>
        <span :class="labelClass">{{ $t('profile_user_communities') }}</span>
        <div class="tw-flex flex-col gap-2 mt-4">
          <div
            class="tw-flex items-center gap-3 rounded-lg border border-slate-200 p-3"
            v-for="(community, index) of user.communities"
            :key="'community' + index"
          >
            <icon class="shrink-0" name="fort" size="18" :fill="community.color"/>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium text-slate-800 truncate">{{ community.name }}</div>
              <div class="text-xs" :style="{ color: community.color }">
                {{
                  community.managers.includes(user.id)
                    ? $t('user_panel_community_manager')
                    : $t('user_panel_community_member')
                }}
              </div>
              <router-link
                class="text-xs text-primary no-underline hover:underline"
                :to="'/communities/' + community.value"
                @click="trackClaimVisit('ClaimedCommunity', community.name)"
              >{{ $t('profile_settings_community_view') }}
              </router-link>
            </div>
            <DsButton
              label=""
              icon="trash"
              variant="rounded-secondary"
              size="small"
              @click="openRemoveMemberModal(community)"
            />
          </div>
        </div>
      </template>

      <!-- ── Activity log ── -->
      <template v-if="isOwner && userActivities && Object.keys(userActivities).length">
        <div class="my-8 border-t border-slate-200"></div>
        <span :class="labelClass">{{ $t('user_panel_latest_activities') }}</span>
        <div class="tw-flex flex-col gap-4 mt-4">
          <div v-for="(activity, date) of userActivities" :key="date">
            <div class="text-xs font-semibold text-slate-500">{{ date }}</div>
            <ul class="list-disc pl-5 mt-1 mb-0 text-sm leading-6 text-slate-600">
              <li v-for="(value, valueIndex) of activity" :key="valueIndex">{{ value.description }}</li>
            </ul>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { fetchUserDetail, unsubscribeUserFromWeeklyDigest } from "@/api/config";
  import IconButton from "@/Domain/Shared/Components/IconButton.vue";
  import { actorLogo } from "@/util/actor";
  import { trackHeapEvent, trackMatomoEvent } from "@/util/analytics";
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from "@/store/modules/ui";
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from "@/store/modules/conversations";
  import { Conversations } from "@/api/conversations";
  import TranslationsMixin from "@/util/TranslationsMixin";
  import onboardingMixin from "@/util/OnboardingMixin";
  import Loading from "@/components/Dashboard/ConceptMap/Loading.vue";
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from "@/constants/analytics-constants";
  import Icon from "@/components/Icon/Icon.vue";
  import { toDatetimeString } from "@/util/date";
  import { humanizeUserActivity } from "@/util/user-analytics";
  import MODAL_IDS from '@/constants/modal-ids'
  import { removeMember } from '@/api/communities'
  import UserMixin from '@/util/UserMixin'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'

  // Long enough that the clamp actually hides something. Kept at the panel's original threshold so
  // the same bios get a toggle as before.
  const BIO_TOGGLE_THRESHOLD = 150

  declare interface BaseComponentData {
    user: any,
    showMoreNonProductClaims: boolean,
    showMoreProductClaims: boolean,
    isUserDataLoading: boolean,
    hidePhoneNumber: boolean,
    showFullBio: boolean,
    isStartingConversation: boolean,
  }

  export default defineComponent({
    name: 'UserInformationSidePanel',
    components: {
      Icon,
      Loading,
      IconButton,
    },
    props: {
      userIdOfUserToDisplay: {
        default: false,
      },
      showPhoneNumber: {
        type: Boolean,
      },
      highlightPhoneNumber: {
        type: Boolean,
      }
    },
    mixins: [TranslationsMixin, onboardingMixin, UserMixin, TaxonomyMixin],
    data: (): BaseComponentData => {
      return {
        user: {},
        showMoreNonProductClaims: false,
        showMoreProductClaims: false,
        // True from the start: the flag used to begin false, so the body rendered once against the
        // empty `user` before `fetchUser` flipped it — a flash of a nameless profile reporting no
        // activity, which is indistinguishable from a user who really has none.
        isUserDataLoading: true,
        hidePhoneNumber: true,
        showFullBio: false,
        isStartingConversation: false,
      }
    },
    mounted() {
      this.applyPhoneNumberVisibility()
      this.fetchUser()

      this.$bus.on('confirmAction', this.handleConfirmAction)
    },
    beforeUnmount() {
      this.$bus.off('confirmAction')
    },
    watch: {
      userIdOfUserToDisplay() {
        this.fetchUser()
      }
    },
    computed: {
      /** One definition of the section overline, used by every labelled block in the panel. */
      labelClass() {
        return 'block text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-400'
      },
      panelMetaData() {
        return this.$store.state.ui.sidePanel.metaData || {}
      },
      /**
       * Opened from a place that wants the number read, not revealed — the actor contact row. The
       * eye control is dropped there rather than starting open, since there is nothing to re-hide.
       */
      highlightsPhoneNumber() {
        return !!this.panelMetaData.highlightPhoneNumber
      },
      ecosystemName() {
        return (this.$store.state.config && this.$store.state.config.name) || this.$t('user_panel_platform')
      },
      /** The person's position, as the header subtitle: what they do, then where they do it. */
      roleLine() {
        return [this.experienceLevelName, this.departmentName].filter(Boolean).join(' · ')
      },
      /**
       * The organisation this person represents, shown under their name. Their first claimed actor:
       * the claimed-actors block below is the full list, this is the one-line answer to "who is
       * this person here on behalf of".
       */
      primaryOrganisation() {
        return this.nonProductClaims[0] || null
      },
      userInitials() {
        return this.initialsFor(this.user.name)
      },
      /**
       * When this person started representing the organisation shown under their name — the claim's
       * own created_at, not their ecosystem join date, which is a different and usually earlier
       * thing. Month and year only: the exact day is not what the line is for.
       */
      representingSince() {
        return this.formatMonthYear(this.primaryOrganisation && this.primaryOrganisation.created_at)
      },
      /**
       * The most recent day this person did anything, from the activity log.
       *
       * `activities` is keyed by `DATE(created_at)` — see UserController::getActivitiesForUser —
       * so the keys are `YYYY-MM-DD` and sort lexically into chronological order.
       *
       * `last_activity` is the fallback, not the source: it is written only by the login listener
       * (User::updateLastActivity, called from LogAuthenticated), so someone who stays signed in
       * for months reads as inactive by that measure. It still covers the case where the log is
       * empty or was not returned.
       */
      lastActiveOn() {
        // Keys are `YYYY-MM-DD`, so lexical order is chronological and the last is the newest.
        const loggedDates = Object.keys(this.user.activities || {}).sort()
        const newestLogged = loggedDates[loggedDates.length - 1]

        // The most recent of the two, not one preferred over the other. Both are signals of the
        // same thing and neither is a superset: `activity_log` is written by tracked page views
        // (and capped at 50 rows per user by the endpoint), `last_activity` only by the login
        // listener. Reading the log first meant a stale log entry hid a much more recent login —
        // a user who signed in last week but whose oldest 50 log rows are from March read as
        // "active over 6 months ago".
        //
        // Both are per-ecosystem — activity_log is filtered on ecosystem_id, last_activity is a
        // team_users column — so someone busy in one ecosystem legitimately reads as quiet here.
        const candidates = [newestLogged, this.user.last_activity]
          .map(value => this.parseDate(value))
          .filter(Boolean)

        if (!candidates.length) {
          return ''
        }

        return new Date(Math.max(...candidates.map(date => date.getTime())))
      },
      /**
       * Whole calendar months between the last activity and now — 0 is "this month".
       *
       * Calendar months rather than elapsed days, because that is what the labels claim: on a
       * rolling 30-day count a visit on the 31st of last month would read as "this month", and
       * a visit yesterday could read as last month's.
       */
      monthsSinceLastActive() {
        const last = this.parseDate(this.lastActiveOn)

        if (!last) {
          return null
        }

        const now = new Date()

        return (now.getFullYear() - last.getFullYear()) * 12 + (now.getMonth() - last.getMonth())
      },
      isActiveThisMonth() {
        return this.monthsSinceLastActive !== null && this.monthsSinceLastActive <= 0
      },
      /**
       * A ladder rather than a date: this month, last month, N months ago, then everything past
       * six months in one bucket. Coarse on purpose — how recently someone was here is useful,
       * the exact day they were is nobody's business.
       */
      activityLabel() {
        const months = this.monthsSinceLastActive

        console.log(months)
        if (months === null) {
          return this.activityFallbackLabel
        }

        // `<= 0` and not `=== 0`: a timestamp in the future is clock skew, not the future.
        if (months <= 0) {
          return this.$t('user_panel_active_this_month')
        }

        if (months === 1) {
          return this.$t('user_panel_active_last_month')
        }

        // `<= 6`, so a gap of exactly six calendar months — which can be as little as five months
        // and a day — is named rather than lumped into "over six months".
        if (months <= 6) {
          return this.$t('user_panel_active_months_ago', { count: months })
        }

        return this.$t('user_panel_active_over_six_months')
      },
      /**
       * The last resort when neither source has anything: the date they joined the ecosystem.
       *
       * Both activity sources are per-ecosystem — activity_log is filtered on ecosystem_id, and
       * last_activity is a team_users column written only by the login listener — so someone who
       * has never signed in *here* has neither, however busy they are elsewhere.
       *
       * Labelled "Joined", not folded into the activity ladder: a join date is not activity, and
       * presenting it as one would state something we do not know.
       */
      activityFallbackLabel() {
        const joined = this.formatMonthYear(this.user.joined_at)

        return joined
          ? this.$t('user_panel_joined_month', { date: joined })
          : this.$t('user_panel_no_activity')
      },
      canStartConversation() {
        return !!this.user.id && this.user.id !== this.$store.getters.userId
      },
      /** The profile's own channels, as the square buttons beside the conversation action. */
      socialLinks() {
        return this.userProfileSocialMediaLinks.filter(link => !!link.value)
      },
      /**
       * The email address is deliberately not on this panel: conversations are the way to reach
       * someone here, and the drawer's own action starts one. Only the phone number is left, and
       * only behind its reveal control.
       */
      hasContactDetails() {
        return !!this.user.phone
      },
      bioIsTruncatable() {
        return !!this.getUserBioI18n && this.getUserBioI18n.length >= BIO_TOGGLE_THRESHOLD
      },
      /**
       * `|| []` rather than reading the collection straight: `state.taxonomies.departments` is only
       * populated from `window.ALL_TAXONOMIES`, which not every page exposes — and a `.find` on
       * undefined throws inside a computed, which takes the whole panel down with it rather than
       * this one line.
       */
      departmentName() {
        if (!this.user.department) return ''

        const departments = this.$store.state.taxonomies.departments || []
        const department = departments.find(d => d.id === Number(this.user.department))
        if (!department) return this.user.department

        return this.getTranslatedTaxonomyName(department)
      },
      experienceLevelName() {
        if (!this.user.function) return ''

        const experienceLevels = this.$store.state.taxonomies.experienceLevels || []
        const experienceLevel = experienceLevels.find(e => e.id === Number(this.user.function))
        if (!experienceLevel) return this.user.function

        return this.getTranslatedTaxonomyName(experienceLevel)
      },
      /**
       * Every motivation and every expertise, not just the first of each.
       *
       * The panel used to read `[0]` and print one value under a singular label, so a member who
       * had filled in three reasons for being here was shown one of them.
       */
      motivationItems() {
        return (this.user.motivation || [])
          .filter(item => item && item.name)
          .map(item => this.getTranslatedTaxonomyName(item))
      },
      expertiseTags() {
        return (this.user.expertise || [])
          .filter(item => item && item.name)
          .map(item => this.getTranslatedTaxonomyName(item))
      },
      /**
       * Role, tenure and last login. Owners only, exactly as before — these are administrative
       * facts about a member rather than part of the card the member presents.
       */
      membershipRows() {
        if (!this.isOwner) {
          return []
        }

        return [
          { label: this.$t('user_panel_role'), value: this.userRole },
          { label: this.$t('user_panel_member_since'), value: this.formatDate(this.user.joined_at) },
          { label: this.$t('user_panel_last_login'), value: this.formatDate(this.user.last_activity) },
        ].filter(row => !!row.value)
      },
      userRole() {
        if (!window.ROLES) {
          return this.user.role
        }

        return window.ROLES[this.user.role]
      },
      userProfileSocialMediaLinks(): any[] {
        const options = []
        options.push({
          label: 'LinkedIn',
          value: this.user.linkedin_url,
          url: this.user.linkedin_url,
          icon: 'linkedin-logo',
        })

        options.push({
          label: 'Twitter',
          value: this.user.twitter_url,
          url: this.user.twitter_url,
          icon: 'twitter-logo',
        })

        options.push({
          label: 'Instagram',
          value: this.user.instagram_url,
          url: this.user.instagram_url,
          icon: 'instagram-logo',
        })

        return options
      },
      slicedNonProductClaims() {
        if (this.showMoreNonProductClaims) {
          return this.nonProductClaims
        }
        return this.nonProductClaims.slice(0, 5)
      },
      nonProductClaims() {
        if (!this.user || !this.user.claims) {
          return []
        }
        return this.checkForDuplicates(this.user.claims.filter(claim => claim.actor_type !== 'Product'))
      },
      showProductClaims() {
        if (this.nonProductClaims.length > 0) {
          return true
        }
        return this.$store.getters.userCanCreate && this.$store.getters.claimableActorTypes.includes('Product')
      },
      slicedProductClaims() {
        if (this.showMoreProductClaims) {
          return this.productClaims
        }
        return this.productClaims.slice(0, 5)
      },
      productClaimsFromActors() {
        let result = []
        if (this.nonProductClaims.length > 0) {
          for (const nonProductClaim of this.nonProductClaims) {
            if (nonProductClaim && nonProductClaim.products && nonProductClaim.products.length) {
              result = [...result, ...nonProductClaim.products]
            }
          }
        }
        return result
      },
      productClaims() {
        if (!this.user || !this.user.claims) {
          return []
        }
        return this.checkForDuplicates([...this.productClaimsFromActors, ...this.user.claims.filter(claim => claim.actor_type === 'Product')])
      },
      /**
       * Pure, unlike the version this replaces: that one flipped `hidePhoneNumber` from inside a
       * getter, so the reveal depended on the icon having been rendered. The metaData case is
       * handled once in `applyPhoneNumberVisibility` instead.
       */
      showEyeIcon() {
        return this.hidePhoneNumber ? 'eye-slash' : 'eye-solid'
      },
      userActivities() {
        var activities = {}

        if (!this.user.activities || this.user.activities.length == 0) {
          return activities
        }

        Object.keys(this.user.activities).forEach(key => {
          var values = this.user.activities[key]

          activities[key] = []

          if (!values && values.length == 0) {
            return
          }

          activities[key] = values.map(activity => {
            return {
              ...activity,
              description: this.stringifyUserActivity(activity)
            };
          });

          activities[key].sort((a, b) => (a.description > b.description) ? 1 : -1)
        })

        return activities
      },
      getUserBioI18n() {
        let preferredLanguage = this.$store.state?.user?.profile?.preferred_language

        if (!preferredLanguage) {
          return this.user.bio
        }

        let translationObject = this.user.translations

        if (!translationObject) {
          return this.user.bio
        }

        let lang = translationObject.find(lang => lang.locale === preferredLanguage)
        if (!lang) {
          return this.user.bio
        }

        if (!lang.bio) {
          return this.user.bio
        }

        return lang.bio
      },
    },
    methods: {
      trackHeapEvent,
      /**
       * Matomo events for the drawer. Its own category (`User Profile Panel`) because this panel
       * opens from settings, communities, comments, announcements and the actor profiles of every
       * ecosystem — filing it under one ecosystem's engagement category would mix the counts.
       *
       * Who was being looked at is deliberately not in the event name: it would split every count
       * across as many rows as there are users, and the question these answer is what people do
       * with the drawer.
       */
      track(action, name = null) {
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.USER_PROFILE_PANEL, action, name)
      },
      formatDate(value) {
        if (!value) {
          return ''
        }

        return toDatetimeString(new Date(value))
      },
      applyPhoneNumberVisibility() {
        if (this.panelMetaData.showPhoneNumber || this.panelMetaData.highlightPhoneNumber) {
          this.hidePhoneNumber = false
        }
      },
      /** Initials for a name, for the header avatar and for a claimed actor with no logo. */
      initialsFor(name) {
        return String(name || '')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map(word => word[0].toUpperCase())
          .join('')
      },
      claimInitials(claim) {
        return this.initialsFor(claim.name)
      },
      /**
       * The space in a MySQL `YYYY-MM-DD HH:MM:SS` is swapped for a `T` first: Chrome parses the
       * space form anyway, Safari returns an invalid date for it.
       */
      parseDate(value) {
        if (!value) {
          return null
        }

        // `lastActiveOn` hands back a Date it has already resolved; everything else is a string.
        const date = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'))

        return isNaN(date.getTime()) ? null : date
      },
      /** "March 2022" — the granularity the two closing facts are stated at. */
      formatMonthYear(value) {
        const date = this.parseDate(value)

        if (!date) {
          return ''
        }

        return date.toLocaleDateString(this.locale || undefined, { month: 'long', year: 'numeric' })
      },
      /**
       * Open the existing thread with this person, or create one and go to it. The conversations
       * list is refreshed before the route change, or the new thread is missing from the sidebar it
       * lands in — same order ActorDetailContributors uses.
       */
      async startConversation() {
        if (this.isStartingConversation) {
          return
        }

        this.isStartingConversation = true
        trackHeapEvent('userInformationSidePanel.startConversation')
        this.track(MATOMO_EVENT_ACTIONS.CTA_CLICK, 'Start conversation')

        try {
          const conversation = await Conversations.post({ recipient_id: this.user.id })

          await this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.hideUserInformationPanel()
          this.$router.push(`/profile/conversations/${conversation.id}`)
        } catch (errors) {
          console.error(errors)
        } finally {
          this.isStartingConversation = false
        }
      },
      handleConfirmAction($event) {
        if ($event && $event.action === 'unsubscribeWeeklyDigest') {
          this.unsubscribeFromWeeklyDigest()
          return
        }

        this.removeUserFromCommunity($event)
      },
      unsubscribeFromWeeklyDigest() {
        unsubscribeUserFromWeeklyDigest(this.user.id)
          .finally(() => {
            this.fetchUser()
          })
      },
      /** The bio disclosure, as read-more/read-less rather than one toggle event. */
      toggleBio() {
        this.showFullBio = !this.showFullBio
        this.track(
          this.showFullBio ? MATOMO_EVENT_ACTIONS.READ_MORE : MATOMO_EVENT_ACTIONS.READ_LESS,
          'Bio',
        )
      },
      trackClaimVisit(kind, name) {
        trackHeapEvent(`userInformationSidePanel.view${kind}`, { name })
        this.track(MATOMO_EVENT_ACTIONS.CLICK, `${kind} link`)
      },
      trackSocialLink(link) {
        this.track(MATOMO_EVENT_ACTIONS.CLICK, `Channel › ${link.icon.replace('-logo', '')}`)
      },
      openUnsubscribeWeeklyDigestModal() {
        this.track(MATOMO_EVENT_ACTIONS.CTA_CLICK, 'Unsubscribe weekly digest')
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.$t('user_panel_unsubscribe_title', { name: this.user.name }),
          body: this.$t('user_panel_unsubscribe_body', { name: this.user.name }),
          confirmLabel: this.$t('action_yes'),
          type: 'check-confirm',
          cancelLabel: this.$t('action_no'),
          isCancellable: true,
          action: 'unsubscribeWeeklyDigest',
        })

        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      removeUserFromCommunity($event) {
        removeMember($event.communityId, this.user.id)
          .then(() => {
          })
          .finally(() => {
            this.fetchUser()
          })
      },
      openRemoveMemberModal(community) {
        this.track(MATOMO_EVENT_ACTIONS.CTA_CLICK, 'Remove from community')

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.$t('user_panel_remove_member_title', { name: this.user.name }),
          body: this.$t('user_panel_remove_member_body', {
            name: this.user.name,
            community: community.name,
          }),
          confirmLabel: this.$t('action_yes'),
          type: 'check-confirm',
          cancelLabel: this.$t('action_no'),
          isCancellable: true,
          communityId: community.value
        })

        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      toggleShow() {
        this.hidePhoneNumber = !this.hidePhoneNumber

        trackHeapEvent('userInformationSidePanel.togglePhoneNumberVisibility', { hidePhoneNumber: this.hidePhoneNumber })
        // The existing ACTOR_PHONE_NUMBER event stays: it is what the phone-reveal reports across
        // the app, and moving it would break continuity with the figures already collected.
        trackMatomoEvent(MATOMO_EVENT_CATEGORIES.ACTOR_PHONE_NUMBER, MATOMO_EVENT_ACTIONS.BUTTON_CLICK)
      },
      stringifyUserActivity(activity) {
        var action = this.$t('user_panel_activity_viewed') + ' '

        if (activity.properties.method && activity.properties.method != 'GET') {
          // Non "view" actions should be getting their description from the humanizeUserActivity
          action = ''
        }

        return action + humanizeUserActivity(activity.description) + ' (x' + activity.count + ')'
      },
      imageStyle(claim) {
        if (this.companyLogoUrl(claim)) {
          return { backgroundImage: `url(${this.companyLogoUrl(claim)})` }
        }
      },
      companyLogoUrl(claim) {
        return actorLogo(claim)
      },
      fetchUser() {
        this.isUserDataLoading = true
        fetchUserDetail(this.userIdOfUserToDisplay)
          .then(data => {
            this.user = data || {}

            // On the loaded response rather than on mount: an empty one is a panel that showed
            // nothing, and counting it as a view would overstate the drawer's reach.
            if (data && data.id) {
              this.track(MATOMO_EVENT_ACTIONS.MODAL_SHOWN, 'User profile panel')
            }
          })
          .catch(errors => {
            // Without this the panel span forever on any failed request — no catch meant the
            // loading flag was never cleared. An empty user falls through to the unavailable
            // state, which is the honest thing to show when the fetch did not land.
            this.user = {}
            console.error(errors)
          })
          .finally(() => {
            this.isUserDataLoading = false
          })
      },
      checkForDuplicates(array) {
        const filteredArray = []
        const claims = array.map(function (item) {
          return item.name
        })
        const filteredClaims = [...new Set(claims)]

        filteredClaims.map(function (value) {
          // Find
          const claim = array.find(function (item) {
            return item.name === value
          })
          // Set
          if (!filteredArray.includes(claim)) {
            filteredArray.push(claim)
          }
        })
        return filteredArray
      },
      hideUserInformationPanel() {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      }
    }
  })
</script>
